// src/utils/prerenderReady.js
//
// Build-time prerender readiness signal.
//
// The site is a client-rendered SPA: crawlers and AI fetchers that don't run
// JavaScript see an empty shell. `_prerender.mjs` fixes that by rendering each
// route in headless Chromium and snapshotting the DOM (title, meta, canonical,
// H1/body, JSON-LD, breadcrumbs, all of which are injected client-side).
//
// The prerenderer must NOT snapshot a loading spinner. So each indexable page
// component calls usePrerenderReady(<its own data-ready boolean>). When that
// boolean flips true, we raise a global flag + a <html> attribute that the
// prerenderer waits on before capturing.
//
// In a normal user's browser this is a harmless no-op (one boolean + one
// attribute set once). It changes no rendering and no behaviour.

import { useEffect } from "react";

/**
 * Signal that this page's real, indexable content is now in the DOM.
 * @param {boolean} isReady - true once the page's primary data has resolved
 *                            (listings loaded, article fetched, venue loaded…).
 */
export function usePrerenderReady(isReady) {
  useEffect(() => {
    if (!isReady) return;
    if (typeof window === "undefined") return;
    window.__LWD_PRERENDER_READY__ = true;
    // Mirrored onto the root element so the prerenderer can poll cheaply.
    if (typeof document !== "undefined" && document.documentElement) {
      document.documentElement.setAttribute("data-prerender-ready", "1");
    }
  }, [isReady]);
}

export default usePrerenderReady;
